import { motion } from "framer-motion";
import { Eye } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const galleryImages = [
  {
    id: 1,
    url: "https://images.unsplash.com/photo-1776763018970-9fdf66bd4666?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
    altTr: "Lüks otel suite hizmeti - Kıbrıs",
    altEn: "Luxury hotel suite service - Cyprus",
    span: "col-span-2 row-span-2"
  },
  {
    id: 2,
    url: "https://images.unsplash.com/photo-1627224399635-04b73da49323?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    altTr: "Direk dansı performansı - Girne",
    altEn: "Pole dance performance - Kyrenia",
    span: ""
  },
  {
    id: 3,
    url: "https://images.unsplash.com/photo-1771775751121-3091d79073d4?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    altTr: "VIP limuzin transfer - Cyprus",
    altEn: "VIP limousine transfer - Cyprus",
    span: ""
  },
  {
    id: 4,
    url: "https://images.unsplash.com/photo-1603562767384-c8fe55858c2f?crop=entropy&cs=srgb&fm=jpg&w=800&q=80",
    altTr: "Profesyonel dansöz hizmeti",
    altEn: "Professional dancer service",
    span: "col-span-2"
  },
  {
    id: 5,
    url: "https://images.unsplash.com/photo-1540086015396-f0b458832b38?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    altTr: "VIP masa servisi - Gece kulübü",
    altEn: "VIP table service - Nightclub",
    span: ""
  },
  {
    id: 6,
    url: "https://images.unsplash.com/photo-1578760427294-9871d8667bf3?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    altTr: "Gece kulübü atmosferi",
    altEn: "Nightclub atmosphere",
    span: ""
  },
  {
    id: 7,
    url: "https://images.pexels.com/photos/5152595/pexels-photo-5152595.jpeg?auto=compress&cs=tinysrgb&w=800",
    altTr: "Dans performansı - First Lady",
    altEn: "Dance performance - First Lady",
    span: "col-span-2"
  },
  {
    id: 8,
    url: "https://images.pexels.com/photos/34038226/pexels-photo-34038226.jpeg?auto=compress&cs=tinysrgb&w=600",
    altTr: "Romantik otel odası - Kıbrıs",
    altEn: "Romantic hotel room - Cyprus",
    span: ""
  },
  {
    id: 9,
    url: "https://images.unsplash.com/photo-1581197326868-51cafbd1a169?crop=entropy&cs=srgb&fm=jpg&w=600&q=80",
    altTr: "Dansöz silueti",
    altEn: "Dancer silhouette",
    span: ""
  },
  {
    id: 10,
    url: "https://images.pexels.com/photos/5175491/pexels-photo-5175491.jpeg?auto=compress&cs=tinysrgb&w=800",
    altTr: "Parti atmosferi - VIP",
    altEn: "Party atmosphere - VIP",
    span: "col-span-2"
  }
];

const GallerySection = () => {
  const { t, language } = useLanguage();

  return (
    <section id="gallery" className="py-24 bg-[#0F0F0F] relative" data-testid="gallery-section">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-xs font-mono tracking-[0.3em] text-[#00FFFF] uppercase mb-4 block">
            {t("gallery.subtitle")}
          </span>
          <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{t("gallery.title1")}</span>{" "}
            <span className="neon-text-cyan text-[#00FFFF]">{t("gallery.title2")}</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            {t("gallery.description")}
          </p>
        </motion.div>
      </div>

      {/* Gallery Grid with Lazy Loading */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 auto-rows-[180px] md:auto-rows-[220px]">
          {galleryImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className={`relative overflow-hidden group cursor-pointer ${image.span}`}
              data-testid={`gallery-item-${image.id}`}
            >
              <img
                src={image.url}
                alt={language === "tr" ? image.altTr : image.altEn}
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:brightness-110"
                loading="lazy"
                decoding="async"
              />
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Hover Content */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500">
                <div className="w-14 h-14 rounded-full bg-[#FF00FF]/80 flex items-center justify-center transform scale-0 group-hover:scale-100 transition-transform duration-500 delay-100">
                  <Eye size={24} className="text-white" />
                </div>
              </div>

              {/* Border glow on hover */}
              <div className="absolute inset-0 border-2 border-[#FF00FF] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>
          ))}
        </div>
      </div>

      {/* Marquee Text - SEO Keywords */}
      <div className="mt-20 overflow-hidden border-y border-white/10 py-6">
        <div className="animate-marquee whitespace-nowrap flex">
          {[...Array(4)].map((_, i) => (
            <span key={i} className="mx-8 font-['Syne'] text-4xl md:text-6xl font-bold text-white/10 uppercase tracking-widest">
              {language === "tr" 
                ? "KONSOMATRİS • DANSÖZ • VIP TRANSFER • GİRNE • KUZEY KIBRIS • OTEL HİZMETİ •"
                : "HOSTESS • DANCER • VIP TRANSFER • KYRENIA • NORTH CYPRUS • HOTEL SERVICE •"
              }
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
